import './Styles/Frontend.scss';

import Accordion from './Modules/Vendor/Accordion';
import Counter from './Modules/Vendor/Counter/Counter';
import ConsentManager from './Modules/Vendor/ConsentManager/ConsentManager';
import Contact from './Modules/Contact';
import Lightbox from './Modules/Vendor/Lightbox/Lightbox.js';
import Menu from './Modules/Menu';
import './Modules/Vendor/Hover';
import { Blocker, Direction, Scene, Observer, Threshold } from './Modules/Vendor/Scrolly/Scrolly';
import Slider from './Modules/Vendor/Slider/Slider';

type environment = 'development' | 'production';
type context = 'backend' | 'frontend';

document.addEventListener("DOMContentLoaded", () :void => {

    const environment :environment = (document.documentElement.getAttribute('data-environment') ?? 'production') as environment;
    const context :context = (document.documentElement.getAttribute('data-context') ?? 'frontend') as context;

    setTimeout(() :void => document.documentElement.removeAttribute('data-defer'), 250);

    // ###############
    // Consent Manager
    // ###############

    const consentManagerOptions = {
        interfaceLayout: ConsentManager.LAYOUT_BOTTOM_RIGHT,
        interfaceTheme: ConsentManager.THEME_LIGHT,
        debug: false,
        privacyUri: '/datenschutz',
        fingerprintIcon: false
    };

    const consentManagerTags = [
        new ConsentManager.Tag.IFrameTag({
            name: 'googlemaps',
            description: 'Kartendienst des Third-Party-Anbieters Google in den USA',
            label: 'Google Maps',
            uri: '/datenschutz#google-maps',
        }),
        new ConsentManager.Tag.IFrameTag({
            name: 'youtube',
            description: 'Videoplayer des Third-Party-Anbieters Google in den USA',
            label: 'YouTube Videos',
            uri: '/datenschutz#youtube',
        }),
    ];
    const consentManager :ConsentManager = ConsentManager.init(
        // @ts-ignore
        consentManagerOptions,
        consentManagerTags
    );

    consentManager.addTrigger('[href*="/cookies"]');


    // #######
    // Effects
    // #######

    new Counter('.fact__number');

    const globalObserver :Observer = Observer.init();
    globalObserver.addTarget({
        target: document.querySelector('.sitepackage_threestripes'),
        callback: (entry :IntersectionObserverEntry) => {
            if (entry.isIntersecting) {
                setTimeout(() :void => {
                    entry.target.classList.add('-intersecting');
                }, 500);
            }
        }
    });

    const hero = document.querySelector('.textmedia--hero');
    if (hero) {
        Scene.create(hero, {
            dataset: false,
            debug: false,
            onScroll: () => {
                // Animated using scss.
            }
        });
    }

    // ##############
    // Menu & Contact
    // ##############

    Menu.init();
    Contact.init();

    // #######
    // Contact
    // #######

    Accordion.init('.accordion', {
        expandFirstAtStart: false,
        expandSingle: true
    });

    // #######
    // Scrolly
    // #######

    const scrollBlocker = Blocker.init();
    Direction.init({ dataset: true });
    Threshold.init({
        threshold: '.footer__sitemap',
        onChange: (state :any) => {
            document.body.dataset.scrollyFooterThreshold = state.toString();
        }
    });

    // ########
    // Lightbox
    // ########

    const lightbox = new Lightbox({
        debug: false,
        title: false,
        showCallback: (lightbox :Lightbox) => {
            scrollBlocker.on();
        },
        hideCallback: (lightbox :Lightbox) => {
            scrollBlocker.off();
        },
    });

    // #######
    // Sliders
    // #######

    document.querySelectorAll('.menu_subpages')?.forEach((menuSubpages :Node) :void => {
        const pages :HTMLUListElement|null = (menuSubpages as HTMLElement).querySelector('ul');
        if (pages) {
            const slider :Slider = new Slider(pages, {
                debug: false,
                itemSelector: 'li',
                controls: true,
                navigation: false,
                slideMode: Slider.SLIDE_MODE_ROTATE,
                breakpoints: {
                    0: {
                        options: { itemsPerView: 1 },
                    },
                    768: {
                        options: { itemsPerView: 2 },
                    },
                    1168: {
                        options: { itemsPerView: 3 },
                    }
                }
            });
        }
    });

    document.querySelectorAll('.textmedia--fullscreen .textmedia__gallery')?.forEach((fullscreenGallery :Node) :void => {
            const slider :Slider = new Slider(fullscreenGallery, {
                debug: false,
                itemSelector: 'figure',
                controls: true,
                controlsInNavigation: true,
                navigation: true,
                slideMode: Slider.SLIDE_MODE_ROTATE,
                autoplay: true
            });
    });

    // #########################
    // Aria roles and attributes
    // TODO: Should be done using ARIA middleware
    // #########################

    document.querySelectorAll('button, [type="button"], [type="submit"]')
        ?.forEach(button => {
            button.setAttribute('role', 'button');
            if (button.textContent.trim().length) {
                button.setAttribute('aria-label', button.textContent.trim());
            }
        });
    document.querySelectorAll('form')
        ?.forEach(form => {
            form.setAttribute('role', 'form');
            const formLabel :HTMLElement|null = form.querySelector('h1, h2, h3, h4, h5, h6');
            if (formLabel) {
                formLabel.id = `${form.id}__title`;
                form.setAttribute('aria-labelledby', formLabel.id);
            }
            const formFields :NodeList = form.querySelectorAll('input, select, textarea');
            formFields
                ?.forEach(formField => {
                    if (formField.required) {
                        formField.setAttribute('aria-required', 'true');
                    }
                    if ('checkbox' === formField.type || 'radio' === formField.type) {
                        formField.setAttribute('role', formField.type);
                        formField.setAttribute('aria-checked', String(formField.checked));
                        formField.addEventListener('change', () => {
                            formField.setAttribute('aria-checked', String(formField.checked));
                        });
                    }
                    // TODO: for each form group, check if there's a descriptive text, then:
                    // description.id = 'some-id'
                    // formField.setAttribute('aria-describedby', 'some-id');
                });
        });

    // ###############
    // Fade-in effects
    // ###############

    document.querySelectorAll('.page__main > *:not(.sitepackage_hero)')?.forEach(contentElement => {
        globalObserver.addTarget({
            target: contentElement,
            callback: (entry :IntersectionObserverEntry) => {
                if (entry.isIntersecting) {
                    contentElement.classList.add('-visible');
                    globalObserver.unobserve(contentElement);
                }
            }
        });
    });

});
